<script setup>
// Vue twin of <x-radio-rich> (resources/views/components/radio-rich.blade.php +
// App\View\Components\RadioRich). Emits the same markup and class hooks and
// carries no styling of its own: components/radio-options.scss styles both.
//
// The label wraps ONLY the header row; slot content sits outside it as a sibling.
// That asymmetry is load-bearing, not a styling choice: a <label> may not contain
// labelable elements other than its own control, so wrapping the slot is invalid
// markup the moment a caller puts a control in it - as the room picker does, with
// a bed-configuration <select>.
//
// This is what RoomSelector's @click.stop was patching. It wrapped everything in
// one label, so clicking the select toggled the radio, and the handler suppressed
// that. Rendering the correct shape removes the cause: the stylesheet stretches
// the header label's hit area over the whole block (so clicking the description
// still picks the option) and lifts interactive content back above it. All CSS on
// these classes, so this twin inherits the behaviour with no script at all - and
// drops the @click.stop rather than reintroducing the wrap.
//
// The radio stays visible here, unlike RadioBox's hidden one: a full-width block
// reads as content, so the circle is what says "pick one of these".
import { computed } from 'vue';

const props = defineProps({
    // Radio group name, shared by every option in the group and unique to the
    // page. Caller-supplied, matching Blade.
    name: {
        type: String,
        required: true,
    },
    value: {
        type: [String, Number],
        required: true,
    },
    label: {
        type: String,
        required: true,
    },
    // Short text on the right of the header row - a price in every current case.
    detail: {
        type: String,
        default: null,
    },
    modelValue: {
        type: [String, Number],
        default: null,
    },
    required: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue']);

const model = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
});
</script>

<template>
    <div class="radio_rich">
        <label class="radio_rich_header">
            <input
                v-model="model"
                type="radio"
                :name="name"
                :value="value"
                :required="required"
            />
            <span class="radio_rich_label">{{ label }}</span>
            <span v-if="detail" class="radio_rich_detail">{{ detail }}</span>
        </label>

        <!-- Always rendered, where Blade emits it only for a non-empty slot: a
             consumer whose slot content is itself conditional (the room picker's
             configuration select) still passes a slot, so there is nothing to
             test here. radio-options.scss hides it with :empty instead - the same
             trade the pop-out panel's footer makes, decided the same way. -->
        <div class="radio_rich_content">
            <slot />
        </div>
    </div>
</template>
